import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from '../CartContext';
import axios from "axios";
import { API_BASE_URL } from '../config';
import placeholderImage from "../images/Placeholder.png";

const ZIP_RANGES = [
  { state: "AL", min: 35004, max: 36925 },
  { state: "AK", min: 99501, max: 99950 },
  { state: "AZ", min: 85001, max: 86556 },
  { state: "AR", min: 71601, max: 72959 },
  { state: "CA", min: 90001, max: 96162 },
  { state: "CO", min: 80001, max: 81658 },
  { state: "CT", min: 6001, max: 6928 },
  { state: "DE", min: 19701, max: 19980 },
  { state: "FL", min: 32003, max: 34997 },
  { state: "GA", min: 30002, max: 39901 },
  { state: "HI", min: 96701, max: 96898 },
  { state: "ID", min: 83201, max: 83876 },
  { state: "IL", min: 60001, max: 62999 },
  { state: "IN", min: 46001, max: 47997 },
  { state: "IA", min: 50001, max: 52809 },
  { state: "KS", min: 66002, max: 67954 },
  { state: "KY", min: 40003, max: 42788 },
  { state: "LA", min: 70001, max: 71497 },
  { state: "ME", min: 3901, max: 4992 },
  { state: "MD", min: 20601, max: 21930 },
  { state: "MA", min: 1001, max: 5544 },
  { state: "MI", min: 48001, max: 49971 },
  { state: "MN", min: 55001, max: 56763 },
  { state: "MS", min: 38601, max: 39776 },
  { state: "MO", min: 63001, max: 65899 },
  { state: "MT", min: 59001, max: 59937 },
  { state: "NE", min: 68001, max: 69367 },
  { state: "NV", min: 88901, max: 89883 },
  { state: "NH", min: 3031, max: 3897 },
  { state: "NJ", min: 7001, max: 8989 },
  { state: "NM", min: 87001, max: 88439 },
  { state: "NY", min: 10001, max: 14975 },
  { state: "NC", min: 27006, max: 28909 },
  { state: "ND", min: 58001, max: 58856 },
  { state: "OH", min: 43001, max: 45999 },
  { state: "OK", min: 73001, max: 74966 },
  { state: "OR", min: 97001, max: 97920 },
  { state: "PA", min: 15001, max: 19640 },
  { state: "RI", min: 2801, max: 2940 },
  { state: "SC", min: 29001, max: 29948 },
  { state: "SD", min: 57001, max: 57799 },
  { state: "TN", min: 37010, max: 38589 },
  { state: "TX", min: 75001, max: 88595 },
  { state: "UT", min: 84001, max: 84791 },
  { state: "VT", min: 5001, max: 5907 },
  { state: "VA", min: 20101, max: 24658 },
  { state: "WA", min: 98001, max: 99403 },
  { state: "WV", min: 24701, max: 26886 },
  { state: "WI", min: 53001, max: 54990 },
  { state: "WY", min: 82001, max: 83414 },
];

function stateFromZip(zip) {
  const z = Number(String(zip).slice(0, 5));
  if (!Number.isFinite(z)) return null;

  const match = ZIP_RANGES.find(r => z >= r.min && z <= r.max);
  return match?.state ?? null;
}

function Cart() {
  const {
    cart,
    removeFromCart,
    updateQuantity,
    updatePrice,
    cartTotal,
    clearCart,
    itemCount,
    addToCart
  } = useCart();

  const navigate = useNavigate();

  const [maxProductAmount, setMaxProductAmount] = useState({});
  const [currentProductPrice, setCurrentProductPrice] = useState({});
  const [cartProductPrice, setCartProductPrice] = useState({});
  const [cartProductAmount, setCartProductAmount] = useState({});

  const [toggledCalculateContainer, setToggledCalculateContainer] = useState(false);
  const [shippingZip, setShippingZip] = useState('');
  const [shippingOptions, setShippingOptions] = useState([]);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [selectedShipping, setSelectedShipping] = useState(null);
  const [isGroundAdvantageFree, setIsGroundAdvantageFree] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  const [address, setAddress] = useState({
    name: "Test User",
    line1: "123 Test St",
    line2: "",
    city: "New York",
    state: "NY",
    zip: "",
    country: "US",
  });

  function normalizeZip(zip) {
    const m = String(zip || "").trim().match(/\d{5}/);
    return m ? m[0] : "";
  }
  // When a shipping option is applied, set it and log the shipping amount and estimated total (for testing)
  const handleApplyShipping = (opt) => {
    // prefer the server-provided displayAmount when available (this is how free shipping is flagged)
    const normalized = {
      ...opt,
      amount: opt?.displayAmount !== undefined && opt.displayAmount !== null ? Number(opt.displayAmount) : Number(opt.amount),
    };
    setSelectedShipping(normalized);
    // mark free shipping if server flagged it, or fallback to legacy label-based rule
    const free = Boolean(opt?.isFreeShipping) || (opt.label === "USPS Ground Advantage" && cartTotal > 49.99);
    setIsGroundAdvantageFree(free);
    try {
      const shippingAmount = normalized?.amount ? (normalized.amount / 100) : 0;
      const estimatedTotal = (cartTotal || 0) + shippingAmount;
    } catch (e) {
      console.warn('Error logging applied shipping figures', e);
    }
  };

  useEffect(() => {
    // when new options arrive, auto-select the first option if none chosen
    if (shippingOptions && shippingOptions.length > 0) {
      setSelectedShipping(prev => prev || shippingOptions[0]);
    }
  }, [shippingOptions]);

  useEffect(() => {
    // reset options/selection when zip changes
    setShippingOptions([]);
    setSelectedShipping(null);
  }, [shippingZip]);
  useEffect(() => {
    if (!cart.length) return;
    for (let cartItem of cart) {
      axios.get(`${API_BASE_URL}/api/products/${cartItem["_id"]}`)
        .then((response) => {
          setMaxProductAmount(prevState => ({
            ...prevState,
            [response.data._id]: response.data.quantity,
          }));

          setCurrentProductPrice(prevState => ({
            ...prevState,
            [response.data._id]: response.data.price,
          }));


          if (response.data.price != cartItem['price']) updatePrice(cartItem["_id"], response.data.price)
          if (response.data.quantity < cartItem['quantity']) response.data.quantity == 0 ? removeFromCart(cartItem["_id"]) : updateQuantity(cartItem["_id"], response.data.quantity)

        })
        .catch((error) => {
          removeFromCart(cartItem["_id"])
          console.error("Error fetching product details:", error)
        });
    }
    let tempObject = {};
    let tempAmObject = {};
    for (let cartitem of cart) {
      tempObject[cartitem._id] = cartitem.price;
      tempAmObject[cartitem._id] = cartitem.quantity;

    }
    setCartProductPrice(tempObject);
    setCartProductAmount(tempAmObject);
  }, [cart])

  function isTexasZip(zip) {
    if (!zip) return false;
    const z = Number(String(zip).slice(0, 5));
    if (Number.isNaN(z)) return false;
    return (
      (z >= 75001 && z <= 79999) ||
      (z >= 88510 && z <= 88589)
    );
  }

  function calculateTaxCents(subtotalCents, shippingZip) {
    if (!isTexasZip(shippingZip)) return 0;
    const TX_STATE_RATE = 0.0625;
    return Math.round(subtotalCents * TX_STATE_RATE);
  }

  return (
    <section className="cart-container">
      <h1 className="centered-subtitle">Cart</h1>
      <section className="cart-flex-container">

        <article className="cart-products-container">
          <div className="cart-products-eligibility-container">
            {cartTotal > 49.99
              ? (
                <>
                  <p>Your order qualifies for FREE shipping! <i className="bi bi-check-circle-fill"></i></p>
                  <div className="cart-products-eligibility-range-container"><div className="cart-products-eligibility-range-subcontainer cart-products-eligibility-range-subcontainer-full"></div></div>

                </>
              ) :
              (
                <>
                  <p>Your order is <b>${(50 - cartTotal.toFixed(2)).toFixed(2)}</b> away from FREE shipping!</p>
                  <div className="cart-products-eligibility-range-container"><div className="cart-products-eligibility-range-subcontainer" style={{ width: `${(cartTotal.toFixed(2) / 50) * 100}%` }}></div></div>
                  <Link to="/all">Continue Shopping</Link>
                </>
              )}
          </div>
          {cart.length === 0 ? (
            <p>Your cart is empty</p>
          ) : (
            <>

              {cart.map(item => (
                <div key={item.id} className="cart-products-item">
                  <div className="cart-products-item-image" style={{ backgroundImage: `url(${item?.imageURL?.[0]?.includes('/upload/') ? item.imageURL[0].replace("/upload/", '/upload/c_thumb,w_240/') : placeholderImage})` }}></div>
                  <div className="cart-products-item-info">
                    <div className="cart-products-item-title-container">
                      <h3>{item.name.split("(")[0]}</h3>
                      <p>{item.name.split("(")?.[1]?.replace(")", "")}</p>
                    </div>

                    {/* <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => updateQuantity(item.id, +e.target.value)}
                      min="1"
                    /> */}
                    <div className="cart-products-item-input-container">
                      <button onClick={() => {
                        updateQuantity(item.id, (item.quantity - 1))
                      }} className={`${item.quantity == 1 ? "cart-products-item-input-button-disabled" : ""}`}><i className="bi bi-dash-lg"></i></button>
                      <p>{item.quantity}</p>
                      <button onClick={() => {
                        addToCart(item)
                      }} className={`${maxProductAmount?.[item._id] == item.quantity ? "cart-products-item-input-button-disabled" : ""}`}><i className="bi bi-plus-lg"></i></button>
                    </div>
                  </div>


                  <div className="cart-products-item-rightmost-container">
                    <button onClick={() => removeFromCart(item._id)}><i className="bi bi-x-lg"></i></button>
                    <h4>${(item.price * item.quantity).toFixed(2)}</h4>
                  </div>
                </div>
              ))}


            </>
          )}
        </article>
        <article className="cart-summary-container">
          <h2>Summary</h2>
          <div>
            <b>Subtotal ({itemCount} items)</b>
            <b>${cartTotal.toFixed(2)}</b>
          </div>
          <div>
            <b>Shipping*</b>
            <b>{selectedShipping ? `$${isGroundAdvantageFree ? "0.00" : (selectedShipping.amount / 100).toFixed(2)}` : "---"}</b>
          </div>
          <div>
            <b>Taxes*</b>
            <b>{shippingZip != "" ? (isTexasZip(shippingZip) ? "$" + (cartTotal * 0.0625).toFixed(2) : "$0.00") : "---"}</b>
          </div>
          <div onClick={() => setToggledCalculateContainer(!toggledCalculateContainer)} className="cart-summary-calculate-container">
            <b>Calculate</b>
            <i className="bi bi-chevron-down" style={{ transform: `${toggledCalculateContainer ? "rotate(180deg)" : "rotate(0deg)"}` }}></i>
            <div onClick={(e) => e.stopPropagation()} style={{ maxHeight: `${toggledCalculateContainer ? "fit-content" : "0px"}`, padding: `${toggledCalculateContainer ? "1rem" : "0px"}` }}>
              <p>Enter ZIP Code</p>
              <input value={shippingZip} onChange={(e) => setShippingZip(e.target.value)} placeholder="ZIP" />
              <span>
                <button onClick={async (e) => {
                  e.stopPropagation();
                  setAddress(prev => ({ ...prev, zip: shippingZip }));
                  if (!shippingZip) return;
                  try {
                    setShippingLoading(true);
                    let weightSum = 0;
                    for (const cartItem of cart) {
                      await axios.get(`${API_BASE_URL}/api/products/${cartItem["_id"]}`)
                        .then((response) => {
                          const product = response.data;
                          if (product && product?.dimensions?.["Weight (oz)"]) {
                            weightSum += parseFloat(product.dimensions["Weight (oz)"]["$numberDecimal"]) * cartItem.quantity;
                          } else {
                            weightSum += 16 * cartItem.quantity; // default to 1 lb (16 oz) if no weight provided
                          }
                        });
                    }
                    // send cart items so server can compute weight from product data when available
                    const itemsForEstimate = cart.map(i => ({ _id: i._id || i.id, quantity: i.quantity }));
                    const payload = {
                      items: itemsForEstimate,
                      address: {
                        ...address,
                        state: stateFromZip(shippingZip) ??
                          address.state?.trim().toUpperCase(),
                        zip: normalizeZip(shippingZip),
                        country: "US",
                      },
                      parcel: {
                        weight: weightSum || 160,
                        mass_unit: "oz",
                      }
                    };
                    const res = await axios.post(`${API_BASE_URL}/api/shipping/estimate`, payload, { withCredentials: true });
                    const opts = res.data?.options || [];
                    const sortedOpts = Object.values(
                      opts.reduce((acc, item) => {
                        const label = item.label;
                        if (!label) return acc;

                        const nextAmt = Number(item.amount);
                        const curr = acc[label];
                        const currAmt = curr ? Number(curr.amount) : Infinity;

                        if (!curr || nextAmt < currAmt) acc[label] = item;
                        return acc;
                      }, {})
                    ).sort((a, b) =>
                      a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
                    );

                    // Normalize options: prefer server-provided displayAmount (used to indicate free shipping)
                    const normalized = sortedOpts.map(o => ({
                      ...o,
                      // if server provided displayAmount (cents), use it as the amount shown/used client-side
                      amount: o?.displayAmount !== undefined && o.displayAmount !== null ? Number(o.displayAmount) : Number(o.amount),
                      rawAmount: Number(o.amount),
                    }));
                    setShippingOptions(normalized);
                  } catch (err) {
                    console.error('Estimate error', err);
                    setShippingOptions([]);
                  } finally {
                    setShippingLoading(false);
                  }
                }}>{shippingLoading ? 'Estimating...' : 'Estimate'}</button>
              </span>
              {shippingOptions.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <small>Estimates:</small>
                  <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                    {shippingOptions.map(opt => (
                      <li key={opt.id} style={{ marginBottom: 6 }}>
                        <label style={{ cursor: 'pointer' }}>
                          <input type="radio" name="shippingOption" value={opt.id}
                            checked={selectedShipping?.id === opt.id}
                            onChange={() => handleApplyShipping(opt)} />
                          {' '}{opt.label}:
                          {((opt.isFreeShipping) || (opt.amount === 0)) ? " $0.00" : ` $${(opt.amount / 100).toFixed(2)}`}
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
          <div className="cart-summary-total-container">
            <b>Estimated Total</b>
            <b>${(cartTotal + (selectedShipping && !isGroundAdvantageFree ? (selectedShipping.amount / 100) : 0) + Number(isTexasZip(shippingZip) ? cartTotal * 0.0625 : 0)).toFixed(2)}</b>
          </div>
          <button onClick={() => {
            if (!cart || cart.length === 0) return;
            // Navigate to the Enter Shipping flow so the user can provide ZIP and select a ship option
            navigate('/enter-shipping', { state: { shippingZip } });
          }} disabled={isCheckingOut}>
            Checkout
          </button>
          <span>Secured via <i className="bi bi-stripe" style={{ color: "#635BFF" }}></i></span>
          <p><b>Not satisfied? </b>We accept returns and exchanges within 15 days of receiving your order, provided the items are unused and in their original condition. However, due to the nature of crystals and botanicals, some products (like tea and incense) may be final sale for hygiene reasons. For more information, please reach out to us via our <Link to="/contact">Contact</Link> section.</p>
          <i><b>*</b>Calculated during checkout. Any information shown here is just for estimate reasons.</i>
        </article>
      </section>

    </section>
  );
}

export default Cart;